import { useMemo } from "react";
import { useTranslation } from "@coworker/locales";
import AddOnIconPath from "@coworker/apprestructured/src/shared/wrappers/FixaSSRIcon/paths/AddOnIconPath";
import DataChartIconPath from "@coworker/apprestructured/src/shared/wrappers/FixaSSRIcon/paths/DataChartIconPath";
import ClipboardCheckmarkIconPath from "@coworker/apprestructured/src/shared/wrappers/FixaSSRIcon/paths/ClipboardCheckmarkIconPath";
import RoomLayoutIconPath from "@coworker/apprestructured/src/shared/wrappers/FixaSSRIcon/paths/RoomLayoutIconPath";
import ShoppingBagIconPath from "@coworker/apprestructured/src/shared/wrappers/FixaSSRIcon/paths/ShoppingBagIconPath";
import SpeechBubbleIconPath from "@coworker/apprestructured/src/shared/wrappers/FixaSSRIcon/paths/SpeechBubbleIconPath";
import BoxCancelIconPath from "@coworker/apprestructured/src/shared/wrappers/FixaSSRIcon/paths/BoxCancelIconPath";
import ChairIconPath from "@coworker/apprestructured/src/shared/wrappers/FixaSSRIcon/paths/ChairIconPath";
import PencilIconPath from "@coworker/apprestructured/src/shared/wrappers/FixaSSRIcon/paths/PencilIconPath";
import LinkOutIconPath from "@coworker/apprestructured/src/shared/wrappers/FixaSSRIcon/paths/LinkOutIconPath";
import MultiAddOnIconPath from "@coworker/apprestructured/src/shared/wrappers/FixaSSRIcon/paths/MultiAddOnIconPath";
import CheckmarkCircleIconPath from "@coworker/apprestructured/src/shared/wrappers/FixaSSRIcon/paths/CheckmarkCircleIconPath";
import PeopleIconPath from "@coworker/apprestructured/src/shared/wrappers/FixaSSRIcon/paths/PeopleIconPath";
import { TaskTypesRoutes } from "@coworker/apprestructured/src/home/constants/taskTypesRoutes";
import { TaskType } from "@coworker/apprestructured/src/task-management/types/TaskType";
import { TaskTypeNames } from "@coworker/apprestructured/src/task-management/constants/TaskTypeNames";
import { MFAQ_LABEL } from "@coworker/app/src/components/MFAQ/constants";

const NavigationLabels = {
  ADDON: "addonString",
  INSIGHTS: "capitalCaseAddonInsightsString",
  TEST_BUY: "capitalCaseTestBuyString",
  ROOM_SETTINGS: "roomSettingsString",
  MEDIA_MAINTENANCE_ROOM_SETTINGS: "capitalCaseMediaMaintenanceString",
  SHOPPING_TOOLS: "shoppingToolsString",
  CUSTOMER_MFAQ: MFAQ_LABEL,
  PRODUCT_QUALITY_REPORT: "capitalCaseProductQualityReportString",
  PRODUCT_ISSUE: "productString",
  CUSTOM_TASK: "capitalCaseCustomString",
  DASHBOARD: "dashboardString",
  MULTI_ORDER_ADDON: "capitalCaseMultiOrderAddOnsString",
  TASK_OVERVIEW: "taskOverviewString",
  TEAMS_MANAGER: "capitalCaseTeamManagementString",
  TASK: "TaskString",
} as const;

enum MenuIdTypes {
  TASK_OVERVIEW = "task-overview",
  ADDON = "addon",
  DASHBOARD = "dashboard",
  INSIGHTS = "insights",
  TEST_BUY = "test-buy",
  ROOM_SETTINGS = "room-setting",
  SHOPPING_TOOLS = "shopping-tools",
  CUSTOMER_MFAQ = "customer-mfaq",
  PRODUCT_QUALITY_REPORT = "product-quality-report",
  PRODUCT_ISSUE = "product-issue",
  CUSTOM_TASK = "custom-task",
  MULTI_ORDER_ADDONS = "multi-order-addons",
  TEAMS_MANAGER = "teams-manager",
  TASK = "task",
}

export type NavigationItem = {
  id: string;
  icon: (prefix?: string) => React.SVGProps<SVGElement>[];
  href?: string;
  taskType?: string;
  link?: string;
  testId: string;
  disabled?: boolean;
} & (
  | { label: string; labelTranslationKey?: never }
  | { labelTranslationKey: string; label?: never }
);

interface UseNavigationItemsParams {
  isRDT: boolean;
  enableTeamsManagerNavigation: boolean;
  taskTypes: TaskType[] | undefined;
}

export const useNavigationItems = ({
  isRDT,
  enableTeamsManagerNavigation,
  taskTypes,
}: UseNavigationItemsParams): NavigationItem[] => {
  const { t } = useTranslation();

  return useMemo(() => {
    function getTaskTypeId(taskTypeName: string): string | undefined {
      return taskTypes?.find((type) => type.name === taskTypeName)?.id;
    }

    const allItems: NavigationItem[] = [
      {
        id: MenuIdTypes.TASK_OVERVIEW,
        icon: CheckmarkCircleIconPath,
        labelTranslationKey: NavigationLabels.TASK_OVERVIEW,
        href: "/tasks",
        testId: "taskNavigationButton",
      },
      {
        id: MenuIdTypes.MULTI_ORDER_ADDONS,
        icon: MultiAddOnIconPath,
        labelTranslationKey: NavigationLabels.MULTI_ORDER_ADDON,
        href: "/multi-order-addons",
        testId: "multiAddonNavigationButton",
      },
      {
        id: MenuIdTypes.DASHBOARD,
        icon: LinkOutIconPath,
        labelTranslationKey: NavigationLabels.DASHBOARD,
        link: "https://fixadashboard.ingka.com",
        testId: "dashboardNavigationButton",
      },
      {
        id: MenuIdTypes.ADDON,
        icon: AddOnIconPath,
        labelTranslationKey: NavigationLabels.ADDON,
        taskType: TaskTypesRoutes.ADDON,
        testId: "addonNavigationButton",
      },
      {
        id: MenuIdTypes.INSIGHTS,
        icon: DataChartIconPath,
        labelTranslationKey: NavigationLabels.INSIGHTS,
        href: "/insights",
        testId: "insideNavigationButton",
      },
      {
        id: MenuIdTypes.TEST_BUY,
        icon: ClipboardCheckmarkIconPath,
        labelTranslationKey: NavigationLabels.TEST_BUY,
        href: "/testbuy",
        testId: "testBuyNavigationButton",
      },
      {
        id: MenuIdTypes.ROOM_SETTINGS,
        icon: RoomLayoutIconPath,
        labelTranslationKey: NavigationLabels.MEDIA_MAINTENANCE_ROOM_SETTINGS,
        href: "/mediamaintenance",
        testId: "roomSettingsNavigationButton",
      },
      {
        id: MenuIdTypes.SHOPPING_TOOLS,
        icon: ShoppingBagIconPath,
        labelTranslationKey: NavigationLabels.SHOPPING_TOOLS,
        href: "/shoppingtools",
        testId: "shoppingToolsNavigationButton",
      },
      {
        id: MenuIdTypes.CUSTOMER_MFAQ,
        icon: SpeechBubbleIconPath,
        label: NavigationLabels.CUSTOMER_MFAQ,
        href: "/customerquestions",
        testId: "customerQuestionsNavigationButton",
      },
      {
        id: MenuIdTypes.PRODUCT_QUALITY_REPORT,
        icon: BoxCancelIconPath,
        labelTranslationKey: NavigationLabels.PRODUCT_QUALITY_REPORT,
        taskType: TaskTypesRoutes.PRODUCT_QUALITY,
        testId: "productQualityReportNavigationButton",
      },
      {
        id: MenuIdTypes.PRODUCT_ISSUE,
        icon: ChairIconPath,
        labelTranslationKey: NavigationLabels.PRODUCT_ISSUE,
        taskType: TaskTypesRoutes.PRODUCT_ISSUE,
        testId: "productIssueNavigationButton",
        disabled: getTaskTypeId(TaskTypeNames.PRODUCT_ISSUE) === undefined,
      },
      {
        id: MenuIdTypes.CUSTOM_TASK,
        icon: PencilIconPath,
        labelTranslationKey: NavigationLabels.TASK,
        taskType: TaskTypesRoutes.CUSTOM,
        testId: "customNavigationButton",
        disabled: getTaskTypeId(TaskTypeNames.CUSTOM_TASK) === undefined,
      },
    ];

    let items = [...allItems];

    if (isRDT) {
      items = items.filter((item) => item.id !== MenuIdTypes.DASHBOARD);
    }

    if (enableTeamsManagerNavigation) {
      items.push({
        id: MenuIdTypes.TEAMS_MANAGER,
        icon: PeopleIconPath,
        labelTranslationKey: NavigationLabels.TEAMS_MANAGER,
        href: "/teams",
        testId: "teamsManagerNavigationButton",
      });
    }

    return items.sort((a, b) =>
      (a.label ?? t(a.labelTranslationKey ?? "")).localeCompare(
        b.label ?? t(b.labelTranslationKey ?? "")
      )
    );
  }, [isRDT, enableTeamsManagerNavigation, taskTypes, t]);
};
